import { Device } from 'mediasoup-client';
import type { CustomClient } from '../networkextends/CustomClient.js';
import Logger from '../utils/logger.js';

const MODULE = 'MediasoupSfuClient';

type MediasoupMessage = {
    name: 'mediasoup';
    action: string;
    requestId?: number;
    data?: any;
    error?: string;
};

export class MediasoupSfuClient {
    private client: CustomClient;
    private device: Device | null = null;
    private sendTransport: any = null;
    private recvTransport: any = null;
    private producers: Map<string, any> = new Map();
    private consumers: Map<string, any> = new Map();
    private producerSharerById: Map<string, number> = new Map();
    private producerKindById: Map<string, string> = new Map();
    private closedProducerIds: Set<string> = new Set();
    private onVideoTrack: (stream: MediaStream, track: MediaStreamTrack) => void;
    private onAudioTrack: (stream: MediaStream, track: MediaStreamTrack) => void;
    private onProducerClosed?: (producerId: string, sharerId: number, kind: string | null) => void;
    private sharerId: number = 0;

    constructor(
        client: CustomClient,
        onVideoTrack: (stream: MediaStream, track: MediaStreamTrack) => void,
        onAudioTrack: (stream: MediaStream, track: MediaStreamTrack) => void,
        onProducerClosed?: (producerId: string, sharerId: number, kind: string | null) => void
    ) {
        this.client = client;
        this.onVideoTrack = onVideoTrack;
        this.onAudioTrack = onAudioTrack;
        this.onProducerClosed = onProducerClosed;

        this.client.onMediasoupMessage.add((msg: MediasoupMessage) => {
            if (msg.action === 'newProducer' && msg.data?.producerId) {
                const producerId = String(msg.data.producerId);
                const sharerId = Number(msg.data?.sharerId || 0);
                const kind = typeof msg.data?.kind === 'string' ? msg.data.kind : null;
                if (sharerId > 0) {
                    this.producerSharerById.set(producerId, sharerId);
                }
                if (kind) {
                    this.producerKindById.set(producerId, kind);
                }
                this.closedProducerIds.delete(producerId);

                this.consumeProducer(producerId, sharerId, kind).catch((err) => {
                    Logger.warn(MODULE, 'Failed to consume new producer', err);
                });
            }
            if (msg.action === 'producerClosed' && msg.data?.producerId) {
                const producerId = String(msg.data.producerId);
                if (this.closedProducerIds.has(producerId)) {
                    return;
                }

                const incomingSharerId = Number(msg.data?.sharerId || 0);
                const incomingKind = typeof msg.data?.kind === 'string' ? msg.data.kind : null;
                const resolvedSharerId = incomingSharerId || this.producerSharerById.get(producerId) || 0;
                const resolvedKind = incomingKind || this.producerKindById.get(producerId) || null;

                this.closedProducerIds.add(producerId);

                this.closeConsumerByProducer(producerId);
                this.onProducerClosed?.(producerId, resolvedSharerId, resolvedKind);
                this.producerSharerById.delete(producerId);
                this.producerKindById.delete(producerId);

                if (resolvedSharerId > 0 && resolvedSharerId === this.sharerId) {
                    this.sharerId = 0;
                }
            }
        });
    }

    async init() {
        if (this.device) return;
        const response = await this.request('getRouterRtpCapabilities');
        const rtpCapabilities = response?.rtpCapabilities ?? response;
        if (!rtpCapabilities) {
            throw new Error('Missing router RTP capabilities');
        }
        const device = new Device();
        await device.load({ routerRtpCapabilities: rtpCapabilities });
        this.device = device;
    }

    async startBroadcast(stream: MediaStream) {
        await this.init();
        await this.ensureSendTransport();
        await this.startReceiving();

        if (!this.device?.canProduce('video')) {
            throw new Error('Device cannot produce video with current RTP capabilities');
        }

        const liveTracks = stream.getVideoTracks().filter((track) => track.readyState === 'live');
        if (liveTracks.length === 0) {
            throw new Error('No live video tracks available. Re-select camera/screen source.');
        }

        for (const track of liveTracks) {
            if (!this.sendTransport) continue;
            if (this.producers.has(track.id)) continue;

            const producer = await this.sendTransport.produce({
                track,
                appData: { trackId: track.id, sharerId: this.sharerId },
                stopTracks: false,
            });
            this.producers.set(track.id, producer);
        }
    }

    setSharerId(sharerId: number) {
        this.sharerId = sharerId;
    }

    isBroadcasting() {
        return this.producers.size > 0;
    }

    async startReceiving() {
        await this.init();
        await this.ensureRecvTransport();
        await this.consumeExisting();
    }

    async ensureSendTransport() {
        if (!this.device) {
            throw new Error('mediasoup device not initialized');
        }
        if (this.sendTransport) return;

        const transportInfo = await this.request('createTransport');
        this.sendTransport = this.device.createSendTransport(transportInfo);

        this.sendTransport.on('connect', async ({ dtlsParameters }: any, callback: () => void, errback: (err: any) => void) => {
            try {
                await this.request('connectTransport', {
                    transportId: this.sendTransport.id,
                    dtlsParameters,
                });
                callback();
            } catch (err) {
                errback(err);
            }
        });

        this.sendTransport.on('produce', async ({ kind, rtpParameters, appData }: any, callback: (data: any) => void, errback: (err: any) => void) => {
            try {
                const produceResponse = await this.request('produce', {
                    transportId: this.sendTransport.id,
                    kind,
                    rtpParameters,
                    appData,
                });
                const producerId = typeof produceResponse === 'string'
                    ? produceResponse
                    : produceResponse?.producerId;

                if (typeof producerId !== 'string' || producerId.length === 0) {
                    throw new Error('Invalid produce response: missing producerId');
                }

                callback({ id: producerId });
            } catch (err) {
                errback(err);
            }
        });
    }

    async ensureRecvTransport() {
        if (!this.device) {
            throw new Error('mediasoup device not initialized');
        }
        if (this.recvTransport) return;

        const transportInfo = await this.request('createTransport');
        this.recvTransport = this.device.createRecvTransport(transportInfo);

        this.recvTransport.on('connect', async ({ dtlsParameters }: any, callback: () => void, errback: (err: any) => void) => {
            try {
                await this.request('connectTransport', {
                    transportId: this.recvTransport.id,
                    dtlsParameters,
                });
                callback();
            } catch (err) {
                errback(err);
            }
        });
    }

    async consumeExisting() {
        const response = await this.request('listProducers');
        const producerList = Array.isArray(response?.producers) ? response.producers : response;

        if (!Array.isArray(producerList)) {
            return;
        }

        for (const entry of producerList) {
            const producerId = typeof entry === 'string' ? entry : entry?.producerId;
            const sharerId = typeof entry === 'string' ? 0 : Number(entry?.sharerId || 0);
            const kind = typeof entry === 'string' ? null : (typeof entry?.kind === 'string' ? entry.kind : null);
            if (producerId) {
                if (sharerId > 0) {
                    this.producerSharerById.set(String(producerId), sharerId);
                }
                if (kind) {
                    this.producerKindById.set(String(producerId), kind);
                }
                this.closedProducerIds.delete(String(producerId));
                try {
                    await this.consumeProducer(String(producerId), sharerId, kind);
                } catch (err) {
                    Logger.warn(MODULE, `Skipping producer ${producerId} during consumeExisting`, err);
                }
            }
        }
    }

    async consumeProducer(producerId: string, sharerIdHint: number = 0, kindHint: string | null = null) {
        if (!this.device) return;
        await this.ensureRecvTransport();
        if (!this.recvTransport) return;

        if (this.consumers.has(producerId)) return;

        const consumeData = await this.request('consume', {
            transportId: this.recvTransport.id,
            producerId,
            rtpCapabilities: this.device.rtpCapabilities,
        });

        const consumer = await this.recvTransport.consume({
            id: consumeData.id,
            producerId: consumeData.producerId,
            kind: consumeData.kind,
            rtpParameters: consumeData.rtpParameters,
        });

        this.consumers.set(producerId, consumer);

        const stream = new MediaStream([consumer.track]);
        const sharerId = Number(consumeData.sharerId || sharerIdHint || 0);
        const kind = (typeof consumeData.kind === 'string' ? consumeData.kind : kindHint) || null;
        if (sharerId > 0) {
            this.producerSharerById.set(producerId, sharerId);
        }
        if (kind) {
            this.producerKindById.set(producerId, kind);
        }
        this.closedProducerIds.delete(producerId);

        const handleConsumerStop = () => {
            if (this.closedProducerIds.has(producerId)) {
                return;
            }

            const resolvedSharerId = this.producerSharerById.get(producerId) || sharerId;
            const resolvedKind = this.producerKindById.get(producerId) || kind;
            this.closedProducerIds.add(producerId);

            this.closeConsumerByProducer(producerId);
            this.onProducerClosed?.(producerId, resolvedSharerId, resolvedKind);
            this.producerSharerById.delete(producerId);
            this.producerKindById.delete(producerId);
            if (resolvedSharerId > 0 && resolvedSharerId === this.sharerId) {
                this.sharerId = 0;
            }
        };

        consumer.on('producerclose', handleConsumerStop);
        consumer.on('trackended', handleConsumerStop);

        if (consumer.kind === 'video') {
            (stream as any)._sharerId = sharerId;
            this.onVideoTrack(stream, consumer.track);
        } else if (consumer.kind === 'audio') {
            (stream as any)._sharerId = sharerId;
            this.onAudioTrack(stream, consumer.track);
        }

        await this.request('resumeConsumer', { consumerId: consumer.id });
    }

    closeConsumerByProducer(producerId: string) {
        const consumer = this.consumers.get(producerId);
        if (!consumer) return;
        try {
            consumer.close();
        } catch (err) {
            Logger.debug(MODULE, 'Failed to close consumer', err);
        }
        this.consumers.delete(producerId);
    }

    async stopBroadcast() {
        for (const [trackId, producer] of this.producers.entries()) {
            const producerId = typeof producer?.id === 'string'
                ? producer.id
                : producer?.id?.producerId;
            const producerKind = typeof producer?.kind === 'string' ? producer.kind : null;

            try {
                if (typeof producerId === 'string' && producerId.length > 0) {
                    await this.request('closeProducer', {
                        producerId,
                        sharerId: this.sharerId,
                        kind: producerKind,
                    });
                } else {
                    Logger.warn(MODULE, `Skipping closeProducer for track ${trackId}: invalid producer id`, producer?.id);
                }
            } catch (err) {
                Logger.warn(MODULE, `Failed to close producer for track ${trackId}`, err);
            }
            try {
                producer.close();
            } catch {
                // ignore
            }
        }
        this.producers.clear();
    }

    private async request(action: string, data?: any): Promise<any> {
        const response = await this.client.sendMediasoupRequest(action, data || {});
        if (response?.error) {
            throw new Error(response.error);
        }
        return response?.data ?? response;
    }
}
